import axios, { AxiosResponse } from 'axios';
import {
  User,
  Bot,
  BotStatus,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  BotConfig,
  TradingPair,
  CreateBotRequest,
  NotificationSettings,
  LLMDecision,
  AutoConfigValues,
  StartBotResponse
} from '../types/api';
import { API_BASE_URL } from '../config/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't reload for auth endpoints - let the login form handle the error display
      const isAuthEndpoint = error.config?.url?.includes('/api/auth/');
      const hadToken = localStorage.getItem('access_token') !== null;

      if (!isAuthEndpoint && hadToken) {
        // Only clear and reload if user was logged in and token expired
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response: AxiosResponse<LoginResponse> = await api.post('/api/auth/login', credentials);
    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<User> => {
    const response: AxiosResponse<User> = await api.post('/api/auth/register', userData);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response: AxiosResponse<User> = await api.get('/api/user/me');
    return response.data;
  },
};

// Bot APIs
export const botAPI = {
  getBots: async (): Promise<Bot[]> => {
    const response: AxiosResponse<Bot[]> = await api.get('/api/bots');
    return response.data;
  },

  getBot: async (botId: number): Promise<Bot> => {
    const response: AxiosResponse<Bot> = await api.get(`/api/bots/${botId}`);
    return response.data;
  },

  createBot: async (botData: CreateBotRequest): Promise<Bot> => {
    const response: AxiosResponse<Bot> = await api.post('/api/bots', botData);
    return response.data;
  },

  updateBot: async (botId: number, botData: CreateBotRequest): Promise<Bot> => {
    const response: AxiosResponse<Bot> = await api.put(`/api/bots/${botId}`, botData);
    return response.data;
  },

  deleteBot: async (botId: number): Promise<void> => {
    await api.delete(`/api/bots/${botId}`);
  },

  startBot: async (botId: number, forceStart: boolean = false): Promise<StartBotResponse> => {
    const response: AxiosResponse<StartBotResponse> = await api.post(
      `/api/bots/${botId}/start`,
      null,
      { params: { force_start: forceStart } }
    );
    return response.data;
  },

  stopBot: async (botId: number): Promise<{ message: string }> => {
    const response: AxiosResponse<{ message: string }> = await api.post(`/api/bots/${botId}/stop`);
    return response.data;
  },

  getBotStatus: async (botId: number): Promise<{ 
    bot: Bot; 
    runtime_status: BotStatus;
    detailed_info?: {
      last_llm_decision?: any;
      risk_analysis?: any;
      logs?: {
        recent: any[];
        errors: any[];
        warnings: any[];
        stats: {
          total_logs: number;
          error_count: number;
          warning_count: number;
          info_count: number;
          debug_count: number;
        };
      } | null;  // Can be null when lock can't be acquired
      current_auto_config?: AutoConfigValues;
      log_error?: string;  // Error message if logs couldn't be retrieved
    };
    llm_decisions?: LLMDecision[];
  }> => {
    const response = await api.get(`/api/bots/${botId}/status`);
    return response.data;
  },

  getTechnicalIndicators: async (botId: number): Promise<{
    bot_id: number;
    timestamp: string;
    indicators: Record<string, {
      timestamp: string;
      technical_data: Record<string, any>;
      validations: Record<string, {
        name: string;
        value: number | null;
        status: string;
        is_valid_for_trading: boolean;
        message: string;
        range_info: string;
        color: string;
      }>;
      overall_signal: {
        signal: string;
        color: string;
        message: string;
        valid_percentage: number;
        valid_count: number;
        total_count: number;
      };
    }>;
    configured_pairs: string[];
    total_pairs: number;
    analyzed_pairs: number;
    message?: string;
  }> => {
    const response = await api.get(`/api/bots/${botId}/technical-indicators`);
    return response.data;
  },

  getTechnicalThresholds: async (botId: number): Promise<{
    rsi_oversold: number;
    rsi_overbought: number;
    rsi_extreme_oversold: number;
    rsi_extreme_overbought: number;
    adx_strong_trend: number;
    adx_very_strong: number;
    adx_weak_trend: number;
    momentum_bullish: number;
    momentum_bearish: number;
    momentum_neutral_high: number;
    momentum_neutral_low: number;
    volatility_low: number;
    volatility_normal: number;
    volatility_high: number;
    volatility_extreme: number;
    macd_bullish_threshold: number;
    macd_bearish_threshold: number;
  }> => {
    const response = await api.get(`/api/bots/${botId}/technical-thresholds`);
    return response.data;
  },

  updateTechnicalThresholds: async (botId: number, thresholds: {
    rsi_oversold: number;
    rsi_overbought: number;
    rsi_extreme_oversold: number;
    rsi_extreme_overbought: number;
    adx_strong_trend: number;
    adx_very_strong: number;
    adx_weak_trend: number;
    momentum_bullish: number;
    momentum_bearish: number;
    momentum_neutral_high: number;
    momentum_neutral_low: number;
    volatility_low: number;
    volatility_normal: number;
    volatility_high: number;
    volatility_extreme: number;
    macd_bullish_threshold: number;
    macd_bearish_threshold: number;
  }): Promise<{
    message: string;
    thresholds: Record<string, number>;
    restart_applied: boolean;
  }> => {
    const response = await api.put(`/api/bots/${botId}/technical-thresholds`, thresholds);
    return response.data;
  },

  getBotNews: async (botId: number): Promise<{
    bot_id: number;
    trading_pairs: string[];
    crypto_symbols: string[];
    total_articles: number;
    articles: Array<{
      id: string;
      title: string;
      url: string;
      original_url: string;
      cryptopanic_url: string;
      resolved_url: string;
      created_at: string;
      domain: string;
      kind: string;
      source: Record<string, any>;
      currencies: Array<{
        code: string;
        title: string;
        slug: string;
        url: string;
      }>;
      votes: Record<string, any>;
      is_related: boolean;
    }>;
    timestamp: string;
    error?: string;
  }> => {
    const response = await api.get(`/api/bots/${botId}/news`);
    return response.data;
  },

  // Paginated AI Decisions
  getDecisions: async (botId: number, params?: {
    decision_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    bot_id: number;
    decisions: LLMDecision[];
    total: number;
    offset: number;
    limit: number;
    has_more: boolean;
    stats: {
      total: number;
      initial: number;
      position_monitoring: number;
      risk_management: number;
    };
  }> => {
    const response = await api.get(`/api/bots/${botId}/decisions`, { params });
    return response.data;
  },

  // Paginated Logs
  getLogs: async (botId: number, params?: {
    level?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    bot_id: number;
    logs: Array<{
      timestamp: string;
      level: string;
      logger_name: string;
      message: string;
      module?: string;
      function?: string;
      line?: number;
      exception?: string;
      data?: Record<string, any>;
    }>;
    stats: {
      total_logs: number;
      error_count: number;
      warning_count: number;
      info_count: number;
      debug_count: number;
    };
    total: number;
    offset: number;
    limit: number;
    has_more: boolean;
  }> => {
    const response = await api.get(`/api/bots/${botId}/logs`, { params });
    return response.data;
  },

  // Download Logs as JSON file
  downloadLogs: async (botId: number, params?: {
    level?: string;
    limit?: number;
  }): Promise<Blob> => {
    const response = await api.get(`/api/bots/${botId}/logs/download`, {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  // Clear all logs for a bot
  clearLogs: async (botId: number): Promise<{ message: string; cleared_count: number }> => {
    const response = await api.post(`/api/bots/${botId}/logs/clear`);
    return response.data;
  },
};

// Trading Pairs API
export const tradingPairsAPI = {
  getTradingPairs: async (testnet: boolean = true): Promise<TradingPair[]> => {
    const response: AxiosResponse<TradingPair[]> = await api.get('/api/trading-pairs', {
      params: { testnet }
    });
    return response.data;
  },

  // Get popular trading pairs with additional market info
  getPopularTradingPairs: async (testnet: boolean = true): Promise<TradingPair[]> => {
    const response: AxiosResponse<TradingPair[]> = await api.get('/api/trading-pairs/popular', {
      params: { testnet }
    });
    return response.data;
  },
};

export default api;